import { useState } from 'react';
import { MapPin, Truck, DollarSign, Calendar, Weight, Map, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import LoadMapModal from './LoadMapModal';

const equipmentColors = {
  'Reefer': 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30',
  'Dry Van': 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30',
};

export default function LoadCard({ load, onBook, isBooked, currentUserLoad }) {
  const [showMap, setShowMap] = useState(false);

  return (
    <>
    {showMap && <LoadMapModal load={load} onClose={() => setShowMap(false)} />}
    <div className={cn(
      "glass-card p-5 transition-all duration-300 hover:border-border hover:bg-white/8 group overflow-hidden",
      isBooked && "border-primary/40 gold-glow"
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-muted-foreground mb-1 font-medium tracking-wide uppercase">Load #{load.load_number}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(
              "text-xs font-semibold px-2 py-0.5 rounded-full border",
              equipmentColors[load.equipment_required] || 'text-muted-foreground bg-muted/40 border-border'
            )}>
              {load.equipment_required}
            </span>
            {load.source === 'Market' && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-500/10 border-amber-500/30 text-amber-400">
                Market
              </span>
            )}
            {load.commodity && (
              <span className="text-xs text-muted-foreground">{load.commodity}</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-space font-bold text-primary">${load.rate?.toLocaleString()}</p>
          {load.rate && load.miles && load.miles > 0 && (
            <span className="inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary">
              ${(load.rate / load.miles).toFixed(2)} / mile
            </span>
          )}
        </div>
      </div>

      {/* Route */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
            <p className="text-sm font-medium text-foreground truncate">{load.origin}</p>
          </div>
          <div className="w-px h-4 bg-white/20 ml-[3.5px]" />
          <div className="flex items-center gap-1.5 mt-1">
            <MapPin className="w-2 h-2 text-primary flex-shrink-0" />
            <p className="text-sm font-medium text-foreground truncate">{load.destination}</p>
          </div>
        </div>
        {load.miles && (
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-semibold text-foreground">{load.miles}</p>
            <p className="text-xs text-muted-foreground">miles</p>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {load.pickup_date && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="w-3 h-3" />
            <span>PU: {load.pickup_date}</span>
          </div>
        )}
        {load.weight && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Weight className="w-3 h-3" />
            <span>{load.weight}</span>
          </div>
        )}
      </div>

      {/* Action */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowMap(true)}
          className="p-2 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors flex-shrink-0"
          title="View on Map"
        >
          <Map className="w-4 h-4" />
        </button>
        {load.source === 'Market' ? (
          <a
            href={`tel:${load.broker_phone}`}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm font-semibold hover:bg-amber-500/20 transition-colors"
          >
            <Phone className="w-4 h-4" />
            {load.broker_name} · {load.broker_phone}
          </a>
        ) : currentUserLoad ? (
          <div className="flex-1 text-center py-2 text-xs text-muted-foreground flex items-center justify-center">
            You have an active load. Complete it first.
          </div>
        ) : (
          <Button
            onClick={() => onBook(load)}
            className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all duration-200"
          >
            <Truck className="w-4 h-4 mr-2" />
            Book This Load
          </Button>
        )}
      </div>
    </div>
    </>
  );
}